import { useMemo, useState } from 'react'

import Link from 'next/link'

import { Survey } from '@context/subscription-box/useSurvey'
import type { RichTextField } from '@prismicio/client'

import PricingCardsTextNodes from '@data/i18n/pricing-cards.json'
import SubscriptionBoxData from '@data/subscription-box.json'

import useCart from '@context/useCartContext'
import { lang } from '@lib/constants'
import type { SubscriptionBoxSellingPlanData } from '@lib/types'
import { isCustomizableBoxVariant } from '@lib/utils/isSubscriptionBox'

import RichTextSerializer from '@components/RichTextSerializer'
import SurveyAuthModal from '@components/subscription-box/modals/SurveyAuthModal'

import PricingCard from './PricingCard'

interface Props {
	sectionTitle?: RichTextField
	display?:
		| 'All Subscription Box'
		| 'Gift Subscription Box'
		| 'ACE Subscription Box'
		| 'Featured Subscription Box 1'
		| 'Featured Subscription Box 2'
		| 'Featured Subscription Box 3'
		| 'Featured Subscription Box 4'
		| 'Featured Subscription Box 5'
		| 'Featured Subscription Box 6'
		| 'Featured Subscription Box 7'
		| 'Featured Subscription Box 8'
		| 'Featured Subscription Box 9'
		| 'Featured Subscription Box 10'
}

export default function PricingCards({
	sectionTitle,

	display = 'All Subscription Box'
}: Props) {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const { items } = useCart()

	// The line ID of customizable box that's in the cart
	const customizableBoxInCartLineId = useMemo(
		() => items?.find((item) => isCustomizableBoxVariant(item.merchandise.id))?.id,
		[items]
	)

	// Calculate the selling plans to display (in reality, `display` never changes)
	const plansToDisplay = useMemo(
		() =>
			(SubscriptionBoxData as SubscriptionBoxSellingPlanData[]).filter((boxData) => {
				if (display === 'All Subscription Box') {
					return boxData.sellingPlanTypes.includes('all')
				}
				if (display === 'Gift Subscription Box') {
					return boxData.sellingPlanTypes.includes('gift')
				}
				if (display === 'ACE Subscription Box') {
					return boxData.sellingPlanTypes.includes('ace')
				}
				if (display === 'Featured Subscription Box 1') {
					return boxData.sellingPlanTypes.includes('featured1')
				}
				if (display === 'Featured Subscription Box 2') {
					return boxData.sellingPlanTypes.includes('featured2')
				}
				if (display === 'Featured Subscription Box 3') {
					return boxData.sellingPlanTypes.includes('featured3')
				}
				if (display === 'Featured Subscription Box 4') {
					return boxData.sellingPlanTypes.includes('featured4')
				}
				if (display === 'Featured Subscription Box 5') {
					return boxData.sellingPlanTypes.includes('featured5')
				}
				if (display === 'Featured Subscription Box 6') {
					return boxData.sellingPlanTypes.includes('featured6')
				}
				if (display === 'Featured Subscription Box 7') {
					return boxData.sellingPlanTypes.includes('featured7')
				}
				if (display === 'Featured Subscription Box 8') {
					return boxData.sellingPlanTypes.includes('featured8')
				}
				if (display === 'Featured Subscription Box 9') {
					return boxData.sellingPlanTypes.includes('featured9')
				}
				if (display === 'Featured Subscription Box 10') {
					return boxData.sellingPlanTypes.includes('featured10')
				}

				return false
			}),
		[display]
	)

	const lowestPricePerMonth = useMemo(() => {
		const lowestPrice = plansToDisplay.reduce((lowest: null | number, plan) => {
			if (lowest === null) return plan.pricePerMonth
			return plan.pricePerMonth < lowest ? plan.pricePerMonth : lowest
		}, null)

		return lowestPrice
	}, [plansToDisplay])

	// Open the survey modal when the user adds a customizable box to the cart only if there is not cookie for bbSurveySubmitted as this indicates the user has already submitted the survey in the last 48 hours
	const handleAddToCart = () => {
		const hasModalCookie = document.cookie
			.split(';')
			.some((cookie) => cookie.trim().startsWith('bbSurveySubmitted='))
		if (!hasModalCookie) {
			setIsModalOpen(true)
		}
	}

	return (
		<section className="flex flex-col items-center pricing-cards section" id="choose-plan">
			<div className="overflow-x-auto max-md:w-screen md:container">
				<div className="flex items-end justify-between max-md:container">
					{/* Section title */}
					{sectionTitle?.[0] && (
						<div className="text-3xl font-medium lg:text-6xl">
							<RichTextSerializer content={sectionTitle} />
						</div>
					)}
				</div>

				{/* {lang === 'en-us' && ( */}

				<div className="grid justify-center md:justify-between md:py-8 md:mt-8 overflow-x-auto max-md:px-4 max-md:grid-flow-row-dense md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-16 lg:mt-12">
					{plansToDisplay.map((subscription) => (
						<PricingCard
							key={subscription.productId}
							onAddToCart={handleAddToCart}
							isBestValue={lang !== 'es-es' && subscription.pricePerMonth === lowestPricePerMonth}
							subscription={subscription}
						/>
					))}
				</div>
			</div>

			{customizableBoxInCartLineId && (
				<Survey surveyType="new">
					<SurveyAuthModal
						isModalOpen={isModalOpen}
						closeModal={() => {
							setIsModalOpen(false)
						}}
					/>
				</Survey>
			)}
			{/* )} */}
			{/* {lang !== 'en-us' && <MembershipSignupForm />} */}
		</section>
	)
}
